import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, switchMap, tap } from 'rxjs';
import { environment } from 'environments/environment';
import { COOKIE_SESSION_REQUEST } from 'app/core/auth/session.types';

/**
 * Foto de usuario. Fuente, en orden:
 *   1. USR.FOTO en Oracle  (GET /api/usr-foto/:id — 404 si no tiene)
 *   2. assets/images/users/<id>.png  (fotos históricas del build)
 *   3. assets/images/users/foto.png  (genérica)
 * `version$` cambia al subir una foto, para que todas las vistas (menú,
 * usuarios en línea, selector, adm/acc) la recarguen sin caché.
 */
@Injectable({ providedIn: 'root' })
export class UsrFotoService {
    static readonly GENERICA = 'assets/images/users/foto.png';
    private static readonly LADO = 256;

    private readonly base = `${environment.urlApi.replace(/\/$/, '')}/api/usr-foto`;
    private readonly version = new BehaviorSubject<number>(Date.now());
    readonly version$ = this.version.asObservable();

    constructor(private http: HttpClient) { }

    url(kusr: number): string {
        return `${this.base}/${kusr}?v=${this.version.value}`;
    }

    /** Siguiente fuente tras un error de carga; null si ya está en la genérica. */
    fallback(src: string, kusr: number): string | null {
        if (src.includes('/api/usr-foto/')) return `assets/images/users/${kusr}.png`;
        if (!src.endsWith(UsrFotoService.GENERICA)) return UsrFotoService.GENERICA;
        return null;
    }

    /** Recorta al centro a 256×256 y sube como JPEG (queda en ~20-40 KB). */
    subir(kusr: number, archivo: File): Observable<void> {
        return from(this._redimensionar(archivo)).pipe(
            switchMap(blob => {
                const form = new FormData();
                form.append('foto', blob, 'foto.jpg');
                return this.http.put<void>(`${this.base}/${kusr}`, form, {
                    withCredentials: true,
                    context: new HttpContext().set(COOKIE_SESSION_REQUEST, true),
                });
            }),
            tap(() => this.version.next(Date.now())),
        );
    }

    private async _redimensionar(archivo: File): Promise<Blob> {
        const bitmap = await createImageBitmap(archivo);
        const lado = Math.min(bitmap.width, bitmap.height);
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = UsrFotoService.LADO;
        canvas.getContext('2d')!.drawImage(bitmap,
            (bitmap.width - lado) / 2, (bitmap.height - lado) / 2, lado, lado,
            0, 0, UsrFotoService.LADO, UsrFotoService.LADO);
        bitmap.close();
        return new Promise((resolve, reject) =>
            canvas.toBlob(b => b ? resolve(b) : reject(new Error('No se pudo procesar la imagen')), 'image/jpeg', 0.85));
    }
}
